import { CopyButton, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { translate } from '@/shared/i18n/translate';
import { Button } from '../components/Button';
import { actionIcons, iconSizes, iconStroke, statusIcons } from '../icons';
import type { StatusTone } from '../tokens/statusColorMap';

const toneColor: Record<StatusTone, string> = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'blue',
  neutral: 'gray',
};

const toneIcon = {
  success: statusIcons.success,
  warning: statusIcons.warning,
  danger: statusIcons.error,
  info: statusIcons.info,
  neutral: statusIcons.neutral,
} as const;

const toneAutoClose: Record<StatusTone, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  danger: 12000,
  neutral: 4000,
};

const W3C_TRACE_ID = /\b00-[0-9a-fA-F]{32}-[0-9a-fA-F]{16}-[0-9a-fA-F]{2}\b/;
const LABELED_TRACE_ID = /(?:traceId|поддержке|support|dukungan)\s*[:：]\s*(\S+)/i;

export type ToastOptions = {
  title?: string;
  autoClose?: number | false;
  testId?: string;
  copyValue?: string;
  cause?: unknown;
};

function readErrorTraceId(cause: unknown): string | undefined {
  if (!cause || typeof cause !== 'object' || !('traceId' in cause)) {
    return undefined;
  }
  const value = (cause as { traceId?: unknown }).traceId;
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed && trimmed !== '—' ? trimmed : undefined;
}

function extractTraceIdFromMessage(message: string): string | undefined {
  const w3c = message.match(W3C_TRACE_ID);
  if (w3c?.[0]) {
    return w3c[0];
  }
  const labeled = message.match(LABELED_TRACE_ID);
  const candidate = labeled?.[1]?.trim();
  return candidate && candidate !== '—' ? candidate : undefined;
}

function resolveCopyValue(message: string, options?: ToastOptions): string | undefined {
  const explicit = options?.copyValue?.trim();
  if (explicit && explicit !== '—') {
    return explicit;
  }
  return readErrorTraceId(options?.cause) ?? extractTraceIdFromMessage(message);
}

function ToastBody({ message, copyValue }: { message: string; copyValue?: string }) {
  const CopyIcon = actionIcons.copy;
  const CheckIcon = actionIcons.check;

  if (!copyValue) {
    return message;
  }

  return (
    <Stack gap={8}>
      <Text size="sm">{message}</Text>
      <CopyButton value={copyValue} timeout={2000}>
        {({ copied, copy }) => (
          <Button
            type="button"
            size="xs"
            variant="secondary"
            data-testid="app-toast-copy-trace"
            leftSection={
              copied
                ? <CheckIcon size={iconSizes.inline} stroke={iconStroke} />
                : <CopyIcon size={iconSizes.inline} stroke={iconStroke} />
            }
            onClick={copy}
          >
            {copied
              ? translate('api.error.traceId.copied')
              : translate('api.error.traceId.copy')}
          </Button>
        )}
      </CopyButton>
    </Stack>
  );
}

export function showToast(tone: StatusTone, message: string, options?: ToastOptions) {
  const Icon = toneIcon[tone];
  const copyValue = resolveCopyValue(message, options);
  notifications.show({
    id: options?.testId,
    message: <ToastBody message={message} copyValue={copyValue} />,
    title: options?.title,
    color: toneColor[tone],
    autoClose: options?.autoClose ?? (copyValue && tone === 'danger' ? 20000 : toneAutoClose[tone]),
    withBorder: true,
    icon: <Icon size={iconSizes.inline} stroke={iconStroke} />,
    'data-testid': options?.testId ?? 'app-toast',
  });
}

function errorToast(message: string, options?: ToastOptions): void;
function errorToast(cause: unknown, fallback: string, options?: ToastOptions): void;
function errorToast(
  messageOrCause: unknown,
  fallbackOrOptions?: string | ToastOptions,
  maybeOptions?: ToastOptions,
): void {
  if (typeof messageOrCause === 'string') {
    showToast(
      'danger',
      messageOrCause,
      typeof fallbackOrOptions === 'string' ? maybeOptions : fallbackOrOptions,
    );
    return;
  }

  const fallback = typeof fallbackOrOptions === 'string'
    ? fallbackOrOptions
    : translate('api.error.request_failed');
  const options = typeof fallbackOrOptions === 'string' ? maybeOptions : fallbackOrOptions;
  showToast('danger', fallback, { ...options, cause: options?.cause ?? messageOrCause });
}

export const toast = {
  success: (message: string, options?: ToastOptions) => showToast('success', message, options),
  error: errorToast,
  warning: (message: string, options?: ToastOptions) => showToast('warning', message, options),
  info: (message: string, options?: ToastOptions) => showToast('info', message, options),
};
